import md5 from "md5";
import { AssigneeAvatar, AssigneeType, IAssignee, IDeletedAssignee } from "./models";
import deletedAvatarURL from "wwwroot/img/avatars/unknown.svg";
import { byTextFieldAscAlphanumericCaseInsensitive } from "@gtmhub/util/sorting-utils";
import { Search } from "@webapp/search/models/search.models";

export const assigneeAvatar = (assignee: { picture?: string; email?: string; avatar?: string; color?: string; isDeleted?: boolean }): AssigneeAvatar => {
  if (assignee.isDeleted) {
    return { picture: deletedAvatarURL };
  }

  if (assignee.picture) {
    return { picture: assignee.picture };
  }

  if (assignee.avatar) {
    return { icon: assignee.avatar, color: assignee.color || "#87a6bc" };
  }

  if (assignee.email) {
    return { picture: `//www.gravatar.com/avatar/${md5(assignee.email)}?d=retro` };
  }

  return { picture: deletedAvatarURL };
};

export const respectsAssignPermissions = (assignee: IAssignee, assignPermissions: AssigneeType): boolean =>
  (assignPermissions !== "team" && assignee.type === "user") || (assignPermissions !== "user" && assignee.type === "team");

export const formatSearchAssignees = (searchItems: (Search<"teams"> | Search<"users">)[]): IAssignee[] => {
  return searchItems.map((item) => {
    if (item.collectionName === "users") {
      return {
        ...item.fields,
        id: item.id,
        type: "user",
        name: item.fields.fullName,
        email: item.fields.auth0Cache.email,
        picture: item.fields.picture || (item.fields.auth0Cache && item.fields.auth0Cache.picture),
      };
    }

    return {
      ...item.fields,
      id: item.id,
      type: "team",
      name: item.fields.name,
      email: "",
      picture: item.fields.picture,
    };
  });
};

export const deletedAssignee = (id: string): IDeletedAssignee => ({ id, isDeleted: true });
export const isDeletedAssignee = (a: IAssignee | IDeletedAssignee): a is IDeletedAssignee => "isDeleted" in a;

export function searchAssignees(
  query: string,
  assignees: IAssignee[],
  options: {
    limit: number;
    shouldSkip(assignee: IAssignee): boolean;
  }
): IAssignee[] {
  query = query || "";
  const topMatches: IAssignee[] = [];
  const otherMatches: IAssignee[] = [];

  for (const assignee of assignees) {
    if (options.shouldSkip(assignee)) {
      continue;
    }

    const assigneeNameStartsWithInput = assignee.name.toLowerCase().startsWith(query.toLowerCase());
    const assigneeNameContainsInput = assignee.name.toLowerCase().includes(query.toLowerCase());

    if (assigneeNameStartsWithInput) {
      topMatches.push(assignee);
    } else if (assigneeNameContainsInput) {
      otherMatches.push(assignee);
    }
  }

  return sortAndSliceAssigneeResults(topMatches, otherMatches, options.limit);
}

function sortAndSliceAssigneeResults(topMatches: IAssignee[], otherMatches: IAssignee[], limit: number): IAssignee[] {
  topMatches.sort(byTextFieldAscAlphanumericCaseInsensitive("name"));

  if (topMatches.length >= limit) {
    return topMatches.slice(0, limit);
  }

  otherMatches.sort(byTextFieldAscAlphanumericCaseInsensitive("name"));
  const otherMatchesSliced = otherMatches.slice(0, limit - topMatches.length);

  return topMatches.concat(otherMatchesSliced);
}
